import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formsApi, templatesApi, submissionsApi, adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, FileText, BarChart3, Settings, HelpCircle, Bug, 
  CreditCard, LogOut, Search, MoreVertical, Copy, Trash2, Eye, EyeOff, Edit,
  ChevronDown, LayoutGrid, List, Loader2, Sparkles, User, Crown, Inbox, Users, Shield, Key
} from 'lucide-react';
import { formatDate, truncate, getFormUrl, copyToClipboard } from '../lib/utils';
import OnboardingChecklist from '../components/OnboardingChecklist';
import InstallPwaBanner from '../components/InstallPwaBanner';
import FFLoader from '../components/FFLoader';

const Dashboard = () => {
  const { user, logout, isPaid, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // 'recent' (default — last edited first within each status) or
  // 'alphabetical' (A→Z by title, still keeping published above draft).
  const [sortMode, setSortMode] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [newFormTitle, setNewFormTitle] = useState('');
  const [newFormDesc, setNewFormDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Duplicate-form prompt state. We open a small dialog that pre-fills
  // the input with "{Original} (Copy)" so the user can either keep that
  // default or type something completely new before we create the copy.
  const [duplicateTarget, setDuplicateTarget] = useState(null); // { id, title } | null
  const [duplicateTitle, setDuplicateTitle] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    fetchForms();
    fetchTemplates();
  }, []);

  const fetchForms = async () => {
    try {
      const data = await formsApi.getAll();
      // Fetch actual submission counts for each form
      const formsWithCounts = await Promise.all(
        data.map(async (form) => {
          try {
            const subs = await submissionsApi.getForForm(form.id);
            return { ...form, actual_count: subs.length };
          } catch {
            return { ...form, actual_count: 0 };
          }
        })
      );
      setForms(formsWithCounts);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await templatesApi.getAll({ limit: 6 });
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleCreateForm = async () => {
    if (!newFormTitle.trim()) {
      toast.error('Please enter a form title');
      return;
    }
    
    setCreating(true);
    try {
      const form = await formsApi.create({ 
        title: newFormTitle, 
        description: newFormDesc || null 
      });
      setShowCreateModal(false);
      setNewFormTitle('');
      setNewFormDesc('');
      navigate(`/builder/${form.id}`);
    } catch (error) {
      toast.error('Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const handleUseTemplate = async (templateId) => {
    try {
      const form = await templatesApi.use(templateId);
      toast.success('Form created from template!');
      setShowTemplatesModal(false);
      navigate(`/builder/${form.id}`);
    } catch (error) {
      toast.error('Failed to create form from template');
    }
  };

  const handleDuplicateForm = (formId) => {
    // Open the prompt with a sensible default; user can rename or accept.
    const original = forms.find((f) => f.id === formId);
    setDuplicateTarget({ id: formId, title: original?.title || 'Untitled form' });
    setDuplicateTitle(`${original?.title || 'Untitled form'} (Copy)`);
  };

  const confirmDuplicate = async () => {
    if (!duplicateTarget) return;
    const trimmed = (duplicateTitle || '').trim();
    if (!trimmed) {
      toast.error('Please enter a name for the duplicate');
      return;
    }
    setDuplicating(true);
    try {
      const form = await formsApi.duplicate(duplicateTarget.id, trimmed);
      toast.success('Form duplicated!');
      setForms((prev) => [form, ...prev]);
      setDuplicateTarget(null);
      setDuplicateTitle('');
    } catch (error) {
      toast.error('Failed to duplicate form');
    } finally {
      setDuplicating(false);
    }
  };

  const handleTogglePublish = async (formId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    const action = newStatus === 'published' ? 'published' : 'unpublished';
    
    try {
      const updatedForm = await formsApi.update(formId, { status: newStatus });
      toast.success(`Form ${action}!`);
      setForms(prev => prev.map(f => f.id === formId ? { ...f, status: newStatus } : f));
    } catch (error) {
      toast.error(`Failed to ${action === 'published' ? 'publish' : 'unpublish'} form`);
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form? All submissions will be lost.')) {
      return;
    }
    
    try {
      await formsApi.delete(formId);
      toast.success('Form deleted');
      setForms(prev => prev.filter(f => f.id !== formId));
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const handleCopyLink = async (form) => {
    // Accept either a full form object or a formId for backwards compat.
    const formId = typeof form === 'string' ? form : form?.id;
    const shortId = typeof form === 'string' ? null : form?.short_id;
    const url = getFormUrl(formId, shortId);
    const success = await copyToClipboard(url);
    if (success) {
      toast.success('Link copied to clipboard!');
    } else {
      toast.error('Failed to copy link');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  // Filter and sort forms - published first, then unpublished (draft)
  const filteredForms = forms
    .filter(form => 
      form.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Published forms come first
      if (a.status === 'published' && b.status !== 'published') return -1;
      if (a.status !== 'published' && b.status === 'published') return 1;
      // Within same status: alphabetical OR last-edited (server already
      // returns by updated_at desc, so "recent" is just the natural order).
      if (sortMode === 'alphabetical') {
        return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
      }
      return 0;
    });

  const menuItems = [
    { icon: <Plus className="w-4 h-4" />, label: 'Create', action: () => setShowCreateModal(true) },
    { icon: <Inbox className="w-4 h-4" />, label: 'Submissions', action: () => navigate('/submissions') },
    { icon: <BarChart3 className="w-4 h-4" />, label: 'Analytics', action: () => navigate('/analytics') },
    { icon: <Settings className="w-4 h-4" />, label: 'Settings', action: () => navigate('/settings') },
    { icon: <HelpCircle className="w-4 h-4" />, label: 'Help!', action: () => navigate('/help') },
    // Admin link for admin users
    ...(isAdmin 
      ? [{ icon: <Shield className="w-4 h-4" />, label: 'Admin', action: () => navigate('/admin'), highlight: true }]
      : []
    ),
    // Show Team for paid users, Upgrade for free users
    ...(isPaid 
      ? [{ icon: <Users className="w-4 h-4" />, label: 'Team', action: () => navigate('/team') }]
      : [{ icon: <CreditCard className="w-4 h-4" />, label: 'Upgrade', action: () => navigate('/upgrade') }]
    ),
    // Show API for paid users
    ...(isPaid 
      ? [{ icon: <Key className="w-4 h-4" />, label: 'Developers', action: () => navigate('/developer') }]
      : []
    ),
    { icon: <LogOut className="w-4 h-4" />, label: 'Log out', action: handleLogout },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="dashboard-page">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
              <img src="/logo.png" alt="Fantastic Forms" className="w-8 h-8" />
              <span className="font-bold text-lg tracking-tight hidden sm:inline">Fantastic Forms</span>
            </Link>
            
            {/* Dashboard Menu - All items visible */}
            <div className="hidden lg:flex items-center gap-1">
              {menuItems.map((item, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  size="sm"
                  className={`text-zinc-600 hover:text-white hover:bg-[#FF6B6B] transition-colors ${item.label === 'Log out' ? 'text-red-600 hover:text-white' : ''} ${item.highlight ? 'bg-zinc-900 text-white hover:bg-zinc-800' : ''}`}
                  onClick={item.action}
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </Button>
              ))}
            </div>
            
            {/* Mobile Menu Only */}
            <div className="flex items-center gap-2 lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <LayoutGrid className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {menuItems.map((item, i) => (
                    <DropdownMenuItem key={i} onClick={item.action} className={item.label === 'Log out' ? 'text-red-600' : ''}>
                      {item.icon}
                      <span className="ml-2">{item.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <InstallPwaBanner />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">My Forms</h1>
            <p className="text-zinc-500 text-sm mt-1">Create and manage your forms</p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search forms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-forms-input"
              />
            </div>

            {/* Sort toggle — Recent (last edited) vs A-Z. Two-button pill
                kept compact so it slots next to the search input on
                desktop and stays out of the way on mobile. */}
            <div className="flex border rounded-lg overflow-hidden" data-testid="forms-sort-toggle">
              <button
                onClick={() => setSortMode('recent')}
                className={`px-3 py-2 text-sm whitespace-nowrap ${sortMode === 'recent' ? 'bg-zinc-100 font-medium' : 'hover:bg-zinc-50 text-zinc-600'}`}
                title="Sort by last edited"
                data-testid="forms-sort-recent"
              >
                Recent
              </button>
              <button
                onClick={() => setSortMode('alphabetical')}
                className={`px-3 py-2 text-sm whitespace-nowrap border-l ${sortMode === 'alphabetical' ? 'bg-zinc-100 font-medium' : 'hover:bg-zinc-50 text-zinc-600'}`}
                title="Sort A → Z"
                data-testid="forms-sort-alpha"
              >
                A-Z
              </button>
            </div>
            
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-zinc-100' : 'hover:bg-zinc-50'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-zinc-100' : 'hover:bg-zinc-50'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            
            <Button 
              className="bg-zinc-900 hover:bg-zinc-800"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-form-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Form
            </Button>
          </div>
        </div>

        {/* Quick Start Templates */}
        {forms.length === 0 && !loading && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4">Start with a template</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {templates.map((template) => (
                <Card 
                  key={template.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleUseTemplate(template.id)}
                  data-testid={`template-${template.id}`}
                >
                  <CardContent className="p-4">
                    <div className="aspect-video bg-zinc-100 rounded-lg mb-3 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-zinc-300" />
                    </div>
                    <h3 className="font-medium text-sm truncate">{template.title}</h3>
                    <p className="text-xs text-zinc-500">{template.category}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Forms Grid/List */}
        {loading ? (
          // Skeleton card grid — visually anchored to the same layout
          // grid as the real form cards below (`grid-cols-1 md:2 lg:3`),
          // so when forms finish loading the cards "slot in" instead of
          // a full-page loader → grid swap. Five placeholders covers
          // most users' first row + half of the second on desktop.
          // List view gets a stripe of horizontal rows instead.
          <div
            className={viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }
            data-testid="dashboard-skeleton-grid"
            aria-busy="true"
            aria-label="Loading your forms"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="border border-zinc-200 rounded-lg bg-white p-5 animate-pulse"
                style={{
                  // Stagger each placeholder's pulse so the strip
                  // shimmers as a wave instead of all five blinking in
                  // unison. CSS-only — no extra timer needed.
                  animationDelay: `${i * 120}ms`,
                }}
              >
                {viewMode === 'grid' ? (
                  <>
                    {/* Title placeholder */}
                    <div className="h-5 w-3/5 bg-zinc-200 rounded mb-3"></div>
                    {/* Description (2 lines) */}
                    <div className="h-3 w-full bg-zinc-100 rounded mb-2"></div>
                    <div className="h-3 w-4/5 bg-zinc-100 rounded mb-5"></div>
                    {/* Footer row: status pill + response count + date */}
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-12 bg-zinc-200 rounded-full"></div>
                      <div className="h-3 w-16 bg-zinc-100 rounded"></div>
                      <div className="h-3 w-20 bg-zinc-100 rounded ml-auto"></div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="h-4 w-2/5 bg-zinc-200 rounded mb-2"></div>
                      <div className="h-3 w-3/4 bg-zinc-100 rounded mb-3"></div>
                      <div className="flex items-center gap-3">
                        <div className="h-4 w-12 bg-zinc-200 rounded-full"></div>
                        <div className="h-3 w-16 bg-zinc-100 rounded"></div>
                        <div className="h-3 w-20 bg-zinc-100 rounded ml-auto"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : filteredForms.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              {searchQuery ? 'No forms found' : 'No forms yet'}
            </h3>
            <p className="text-zinc-500 mb-6">
              {searchQuery ? 'Try a different search term' : 'Create your first form to get started'}
            </p>
            {!searchQuery && (
              <div className="flex items-center justify-center gap-3">
                <Button onClick={() => setShowCreateModal(true)} data-testid="empty-create-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Form
                </Button>
                <Button variant="outline" onClick={() => setShowTemplatesModal(true)}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Browse Templates
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
            : 'space-y-4'
          }>
            {filteredForms.map((form) => (
              <Card 
                key={form.id} 
                className={`group hover:shadow-md transition-shadow relative ${
                  form.status !== 'published' ? 'opacity-60 bg-zinc-50' : ''
                }`}
                data-testid={`form-card-${form.id}`}
              >
                {/* Unseen-submission badge — shows the actual delta between
                    current submission count and the last value the user
                    "saw" on the submissions page (tracked in localStorage
                    under ff_seen_{formId}). Click navigates straight to
                    the per-form Submissions view, which will also mark
                    everything as seen and clear the badge. */}
                {(() => {
                  const count = form.actual_count ?? form.submissions_count ?? 0;
                  let seen = 0;
                  try { seen = parseInt(localStorage.getItem(`ff_seen_${form.id}`) || '0', 10) || 0; } catch (e) { /* localStorage may be blocked */ }
                  const delta = Math.max(0, count - seen);
                  if (delta === 0) return null;
                  // Cap display at 99+ so a flood of submissions doesn't blow the layout.
                  const display = delta > 99 ? '99+' : String(delta);
                  return (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/submissions/${form.id}`);
                      }}
                      // Sits OUTSIDE the top-left corner of the card —
                      // half the badge is inset over the card, half sticks
                      // out into the gutter (translate-X/Y -50%). Slightly
                      // larger min-width keeps double-digit counts legible
                      // at this scale.
                      className="absolute top-0 left-0 z-10 -translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-full text-[11px] font-semibold text-white shadow-md ring-2 ring-white hover:scale-110 transition-transform"
                      style={{ backgroundColor: '#FE6A6B' }}
                      aria-label={`${delta} new submission${delta === 1 ? '' : 's'} — view submissions`}
                      title={`${delta} new submission${delta === 1 ? '' : 's'}`}
                      data-testid={`form-new-count-${form.id}`}
                    >
                      {display}
                    </button>
                  );
                })()}

                {/* 3-dot menu positioned at top right */}
                <div className="absolute top-3 right-3 z-10">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 bg-white hover:bg-zinc-100 border border-zinc-200 rounded-md flex items-center justify-center focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 [&[data-state=open]]:opacity-100 [&[data-state=open]]:bg-zinc-100"
                        data-testid={`form-actions-${form.id}`}
                      >
                        <MoreVertical className="w-4 h-4 text-zinc-600" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/builder/${form.id}`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {form.status === 'published' && (
                        <DropdownMenuItem onClick={() => window.open(`/f/${form.short_id || form.id}`, '_blank')}>
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleCopyLink(form)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicateForm(form.id)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/submissions/${form.id}`)}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Submissions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleTogglePublish(form.id, form.status)}>
                        {form.status === 'published' ? (
                          <>
                            <EyeOff className="w-4 h-4 mr-2" />
                            Unpublish
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-2" />
                            Publish
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleDeleteForm(form.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <CardContent className={viewMode === 'grid' ? 'p-6' : 'p-4 flex items-center gap-4'}>
                  <Link 
                    to={`/builder/${form.id}`}
                    className={viewMode === 'grid' ? 'block' : 'flex-1 flex items-center gap-4'}
                  >
                    {viewMode === 'list' && (
                      <div className="w-12 h-12 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-zinc-400" />
                      </div>
                    )}
                    <div className={viewMode === 'list' ? 'flex-1' : ''}>
                      <h3 className="font-semibold text-zinc-900 group-hover:text-zinc-700 transition-colors pr-8">
                        {form.title}
                      </h3>
                      {form.description && (
                        <p className="text-sm text-zinc-500 mt-1">
                          {truncate(form.description, 60)}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400">
                        <span className={`px-2 py-0.5 rounded-full ${
                          form.status === 'published' 
                            ? 'text-white' 
                            : form.status === 'draft'
                            ? 'bg-zinc-100 text-zinc-600'
                            : 'bg-yellow-100 text-yellow-700'
                        }`} style={form.status === 'published' ? { backgroundColor: '#444444' } : {}}>
                          {form.status === 'published' ? 'live' : form.status}
                        </span>
                        <span style={{ color: '#222222' }}>{form.actual_count ?? form.submissions_count ?? 0} responses</span>
                        <span className="ml-auto">(created {new Date(form.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})</span>
                      </div>
                    </div>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Duplicate Form Modal — prompts the user to name the copy so we
          don't blindly suffix "(Copy)" to the original title. */}
      <Dialog
        open={!!duplicateTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDuplicateTarget(null);
            setDuplicateTitle('');
          }
        }}
      >
        <DialogContent data-testid="duplicate-form-modal">
          <DialogHeader>
            <DialogTitle>Duplicate form</DialogTitle>
            <DialogDescription>
              {duplicateTarget && (
                <>Make a copy of <span className="font-medium text-zinc-900">{duplicateTarget.title}</span>. Choose a name for the duplicate.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="duplicate-title">New form name</Label>
            <Input
              id="duplicate-title"
              autoFocus
              value={duplicateTitle}
              onChange={(e) => setDuplicateTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmDuplicate();
                }
              }}
              maxLength={200}
              placeholder="Give the copy a memorable name"
              data-testid="duplicate-title-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDuplicateTarget(null); setDuplicateTitle(''); }}
              disabled={duplicating}
              data-testid="duplicate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDuplicate}
              disabled={duplicating || !duplicateTitle.trim()}
              data-testid="duplicate-confirm-btn"
            >
              {duplicating ? 'Duplicating…' : 'Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Form Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent data-testid="create-form-modal">
          <DialogHeader>
            <DialogTitle>Create a new form</DialogTitle>
            <DialogDescription>
              Give your form a name and start building
            </DialogDescription>
          </DialogHeader>          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Form Title</Label>
              <Input
                id="title"
                placeholder="e.g., Contact Form, Survey, Registration"
                value={newFormTitle}
                onChange={(e) => setNewFormTitle(e.target.value)}
                data-testid="new-form-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="What is this form for?"
                value={newFormDesc}
                onChange={(e) => setNewFormDesc(e.target.value)}
                rows={3}
                data-testid="new-form-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateForm} 
              disabled={creating}
              data-testid="create-form-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates Modal */}
      <Dialog open={showTemplatesModal} onOpenChange={setShowTemplatesModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choose a template</DialogTitle>
            <DialogDescription>
              Start with a pre-built template and customize it
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {templates.map((template) => (
              <Card 
                key={template.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleUseTemplate(template.id)}
              >
                <CardContent className="p-4">
                  <div className="aspect-video bg-zinc-100 rounded-lg mb-3 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-zinc-300" />
                  </div>
                  <h3 className="font-medium text-sm">{template.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{template.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <OnboardingChecklist />
    </div>
  );
};

export default Dashboard;
